/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react'
import {
  Link,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import {
  Badge,
  PageHeader,
  State,
} from '../components/UI'
import { useAuth } from '../components/Auth'
import {
  api,
  businessPath,
  dateTime,
  money,
} from '../lib/api'


type Mode =
  | 'list'
  | 'detail'
  | 'reminders'
  | 'statements'


type Customer = {
  id: string
  name: string
  phone?: string | null
  email?: string | null
  address?: string | null
}


type Receivable = {
  id: string
  customer_id: string
  sale_id?: string | null
  invoice_id?: string | null
  original_amount: string
  amount_paid: string
  balance: string
  due_date?: string | null
  status: string
  created_at: string
}


type AgingRow = {
  receivable: Receivable
  aging_bucket: string
}


type Collection = {
  id: string
  receivable_id: string
  customer_id: string
  amount: string
  payment_method: string
  reference?: string | null
  collected_at: string
}


type StatementEntry = {
  event_type: string
  occurred_at: string
  receivable_id: string
  sale_id?: string | null
  amount: string
  remaining_balance: string
  reference?: string | null
}


type CustomerSummary = {
  customer_id: string
  total_outstanding: string
  open_receivables: Receivable[]
  paid_receivables: Receivable[]
  collections: Collection[]
}


type Reminder={
  channel:string;
  id: string
  customer_id: string
  recipient: string
  amount_outstanding: string
  message?: string | null
  status: string
  error_code?: string | null
  sent_at?: string | null
  created_at: string
}


const bucketLabel = (value: string) =>
  value === 'CURRENT'
    ? 'Current'
    : value === '1_30'
      ? '1?30 days'
      : value === '31_60'
        ? '31?60 days'
        : value === '61_90'
          ? '61?90 days'
          : '90+ days'


export default function ReceivablesPage({
  mode,
}: {
  mode: Mode
}) {
  const { receivableId } = useParams()
  const [searchParams] = useSearchParams()
  const { workspace } = useAuth()

  const [customers, setCustomers] = useState<Customer[]>([])
  const [aging, setAging] = useState<AgingRow[]>([])
  const [receivable, setReceivable] =
    useState<Receivable | null>(null)
  const [collections, setCollections] =
    useState<Collection[]>([])
  const [reminders, setReminders] =
    useState<Reminder[]>([])
  const [statement, setStatement] =
    useState<StatementEntry[]>([])
  const [summary, setSummary] =
    useState<CustomerSummary | null>(null)

  const [selectedCustomer, setSelectedCustomer] =
    useState(searchParams.get('customer') ?? '')
  const [search, setSearch] = useState('')
  const [bucket, setBucket] = useState('')
  const [status, setStatus] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('CASH')
  const [reference, setReference] = useState('')
  const [reminderMessage, setReminderMessage] =
    useState('')


  const customer = (id: string) =>
    customers.find((item) => item.id === id)


  const loadCustomers = async () => {
    const rows = await api<Customer[]>(
      businessPath('/customers'),
    )

    setCustomers(rows)

    return rows
  }


  const loadList = async () => {
    setLoading(true)
    setError('')

    try {
      const [customerRows, agingRows] =
        await Promise.all([
          api<Customer[]>(
            businessPath('/customers'),
          ),
          api<AgingRow[]>(
            businessPath('/receivables/aging'),
          ),
        ])

      setCustomers(customerRows)
      setAging(agingRows)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load debt book.',
      )
    } finally {
      setLoading(false)
    }
  }


  const loadDetail = async () => {
    if (!receivableId) return

    setLoading(true)
    setError('')

    try {
      const debt = await api<Receivable>(
        businessPath(
          `/receivables/${receivableId}`,
        ),
      )

      const [collectionRows, customerRow, agingRows] =
        await Promise.all([
          api<Collection[]>(
            businessPath(
              `/receivables/${receivableId}/collections`,
            ),
          ),
          api<Customer>(
            businessPath(
              `/customers/${debt.customer_id}`,
            ),
          ),
          api<AgingRow[]>(
            businessPath(
              `/receivables/aging?customer_id=${debt.customer_id}`,
            ),
          ),
        ])

      setReceivable(debt)
      setCollections(collectionRows)
      setCustomers([customerRow])
      setAging(agingRows)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load receivable.',
      )
    } finally {
      setLoading(false)
    }
  }


  const loadReminders = async () => {
    setLoading(true)
    setError('')

    try {
      const [customerRows, agingRows, history] =
        await Promise.all([
          api<Customer[]>(
            businessPath('/customers'),
          ),
          api<AgingRow[]>(
            businessPath('/receivables/aging'),
          ),
          api<Reminder[]>(
            businessPath('/receivables/reminders'),
          ),
        ])

      setCustomers(customerRows)
      setAging(agingRows)
      setReminders(history)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load debt reminders.',
      )
    } finally {
      setLoading(false)
    }
  }


  const loadStatement = async (
    customerId: string,
  ) => {
    if (!customerId) {
      setStatement([])
      setSummary(null)
      return
    }

    setLoading(true)
    setError('')

    try {
      const [entries, customerSummary] =
        await Promise.all([
          api<StatementEntry[]>(
            businessPath(
              `/customers/${customerId}/statement`,
            ),
          ),
          api<CustomerSummary>(
            businessPath(
              `/customers/${customerId}/receivables`,
            ),
          ),
        ])

      setStatement(entries)
      setSummary(customerSummary)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load customer statement.',
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    if (mode === 'list') {
      void loadList()
      return
    }

    if (mode === 'detail') {
      void loadDetail()
      return
    }

    if (mode === 'reminders') {
      void loadReminders()
      return
    }

    void (async () => {
      setLoading(true)

      try {
        const rows = await loadCustomers()
        if (rows.length) {
          const requested = searchParams.get('customer')
          const customerId = requested && rows.some((row) => row.id === requested)
            ? requested
            : rows[0].id
          setSelectedCustomer(customerId)
          await loadStatement(customerId)
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load customers.',
        )
        setLoading(false)
      }
    })()
  }, [mode, receivableId])


  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()

    return aging.filter((row) => {
      const item = row.receivable
      const person = customer(item.customer_id)

      const matchesSearch =
        !query ||
        `${person?.name ?? ''} ${person?.phone ?? ''} ${person?.email ?? ''}`
          .toLowerCase()
          .includes(query)

      const matchesBucket =
        !bucket ||
        row.aging_bucket === bucket

      const matchesStatus =
        !status || item.status === status

      const matchesOverdue =
        !overdueOnly || row.aging_bucket !== 'CURRENT'

      const created = item.created_at.slice(0, 10)
      const matchesStart = !startDate || created >= startDate
      const matchesEnd = !endDate || created <= endDate

      return matchesSearch && matchesBucket && matchesStatus &&
        matchesOverdue && matchesStart && matchesEnd
    })
  }, [aging, customers, search, bucket, status, overdueOnly, startDate, endDate])


  const openRows = aging.filter(
    (row) => Number(row.receivable.balance) > 0,
  )

  const totalOutstanding = openRows.reduce(
    (total, row) =>
      total + Number(row.receivable.balance),
    0,
  )

  const overdueTotal = openRows
    .filter(
      (row) => row.aging_bucket !== 'CURRENT',
    )
    .reduce(
      (total, row) =>
        total + Number(row.receivable.balance),
      0,
    )

  const partiallyPaid = openRows.filter(
    (row) => row.receivable.status === 'PARTIALLY_PAID',
  ).length


  const collectPayment = async (
    event: FormEvent,
  ) => {
    event.preventDefault()

    if (!receivable) return

    const value = Number(amount)

    if (
      !Number.isFinite(value) ||
      value <= 0
    ) {
      setError(
        'Enter a collection amount greater than zero.',
      )
      return
    }

    if (value > Number(receivable.balance)) {
      setError(
        'Collection cannot exceed the outstanding balance.',
      )
      return
    }

    setBusy(true)
    setError('')
    setSuccess('')

    try {
      await api(
        businessPath(
          `/receivables/${receivable.id}/collections`,
        ),
        {
          method: 'POST',
          body: JSON.stringify({
            amount,
            payment_method: method,
            reference: reference || null,
          }),
        },
      )

      setAmount('')
      setReference('')
      setSuccess(
        'Collection recorded successfully.',
      )

      await loadDetail()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to record collection.',
      )
    } finally {
      setBusy(false)
    }
  }


  const sendReminder = async (
    event: FormEvent,
  ) => {
    event.preventDefault()

    if (!selectedCustomer) {
      setError('Select a customer first.')
      return
    }

    setBusy(true)
    setError('')
    setSuccess('')

    try {
      await api(
        businessPath(
          `/customers/${selectedCustomer}/reminders`,
        ),
        {
          method: 'POST',
          body: JSON.stringify({
            message:
              reminderMessage.trim() || null,
          }),
        },
      )

      setReminderMessage('')
      setSuccess(
        'The email reminder was accepted by the email service.',
      )

      await loadReminders()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send reminder.',
      )
    } finally {
      setBusy(false)
    }
  }


  if (mode === 'detail') {
    if (loading) {
      return <State title="Loading receivable?" />
    }

    if (!receivable) {
      return (
        <State
          title="Receivable unavailable"
          error
        >
          {error || 'This receivable was not found.'}
        </State>
      )
    }

    const person = customer(
      receivable.customer_id,
    )
    const debtAging = aging.find(
      (row) => row.receivable.id === receivable.id,
    )?.aging_bucket

    return (
      <section className="aad-page">
        <PageHeader
          title="Receivable Detail"
          description="Outstanding balance and collection history."
          action={
            <Link
              className="aad-button aad-button-secondary"
              to="/receivables"
            >
              Back to debt book
            </Link>
          }
        />

        {error && (
          <p className="aad-error">{error}</p>
        )}

        {success && (
          <p className="aad-success">{success}</p>
        )}

        <div className="detail-grid">
          <article className="aad-card aad-card-padding">
            <small>Customer</small>
            <strong>
              {person?.name ?? 'Customer'}
            </strong>
            <p className="aad-muted">
              {person?.phone || person?.email || '?'}
            </p>
          </article>

          <article className="aad-card aad-card-padding">
            <small>Original credit</small>
            <strong>
              {money(receivable.original_amount)}
            </strong>
          </article>

          <article className="aad-card aad-card-padding">
            <small>Collected</small>
            <strong>
              {money(receivable.amount_paid)}
            </strong>
          </article>

          <article className="aad-card aad-card-padding">
            <small>Outstanding</small>
            <strong>
              {money(receivable.balance)}
            </strong>
          </article>
        </div>

        <section className="aad-card aad-card-padding">
          <div className="header-actions">
            <h2 className="aad-section-title">
              Debt information
            </h2>
            <Badge value={receivable.status} />
          </div>

          <p>
            Created: {dateTime(receivable.created_at)}
          </p>

          <p>
            Due date: {receivable.due_date || 'Not set'}
          </p>

          <p>
            Aging: {debtAging ? bucketLabel(debtAging) : 'Current'}
          </p>

          {receivable.sale_id && (
            <Link
              className="aad-button aad-button-secondary"
              to={`/sales/${receivable.sale_id}`}
            >
              Open linked sale
            </Link>
          )}

          {receivable.invoice_id && (
            <Link
              className="aad-button aad-button-secondary"
              to={`/invoices/${receivable.invoice_id}`}
            >
              Open linked invoice
            </Link>
          )}
        </section>

        {Number(receivable.balance) > 0 && (
          <form
            className="aad-card aad-card-padding"
            onSubmit={collectPayment}
          >
            <h2 className="aad-section-title">
              Record collection
            </h2>

            <div className="aad-form-grid">
              <label className="aad-field">
                <span className="aad-label">
                  Amount
                </span>
                <input
                  className="aad-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={amount}
                  onChange={(event) =>
                    setAmount(event.target.value)
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Payment method
                </span>
                <select
                  className="aad-select"
                  value={method}
                  onChange={(event) =>
                    setMethod(event.target.value)
                  }
                >
                  {[
                    'CASH',
                    'POS',
                    'TRANSFER',
                    'OTHER',
                  ].map((value) => (
                    <option key={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Reference
                </span>
                <input
                  className="aad-input"
                  value={reference}
                  onChange={(event) =>
                    setReference(
                      event.target.value,
                    )
                  }
                />
              </label>
            </div>

            <button
              className="aad-button aad-button-gold"
              disabled={busy}
            >
              {busy
                ? 'Recording?'
                : 'Record collection'}
            </button>
          </form>
        )}

        <section className="aad-card aad-card-padding">
          <h2 className="aad-section-title">
            Collection history
          </h2>

          {!collections.length ? (
            <p className="aad-muted">
              No collections have been recorded yet.
            </p>
          ) : (
            <div className="aad-table-wrap">
              <table className="aad-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {dateTime(
                          item.collected_at,
                        )}
                      </td>
                      <td>{money(item.amount)}</td>
                      <td>
                        <Badge
                          value={
                            item.payment_method
                          }
                        />
                      </td>
                      <td>
                        {item.reference || '?'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    )
  }


  if (mode === 'reminders') {
    const customerTotals = new Map<
      string,
      number
    >()

    for (const row of openRows) {
      customerTotals.set(
        row.receivable.customer_id,
        (
          customerTotals.get(
            row.receivable.customer_id,
          ) ?? 0
        ) + Number(row.receivable.balance),
      )
    }

    const debtCustomers = customers.filter(
      (item) =>
        (customerTotals.get(item.id) ?? 0) > 0,
    )

    const selected = customer(
      selectedCustomer,
    )

    return (
      <section className="aad-page">
        <PageHeader
          title="Debt Reminders"
          description="Send real email payment reminders and keep reminder history."
          action={
            <Link
              className="aad-button aad-button-secondary"
              to="/receivables"
            >
              Debt book
            </Link>
          }
        />

        {error && (
          <p className="aad-error">{error}</p>
        )}

        {success && (
          <p className="aad-success">{success}</p>
        )}

        <section className="aad-card aad-card-padding">
          <h2 className="aad-section-title">
            Send email reminder
          </h2>

          <p className="aad-muted">
            Email is connected. SMS and WhatsApp
            reminders are not connected and are not
            marked as sent.
          </p>

          <form onSubmit={sendReminder}>
            <div className="aad-form-grid">
              <label className="aad-field">
                <span className="aad-label">
                  Customer
                </span>
                <select
                  className="aad-select"
                  required
                  value={selectedCustomer}
                  onChange={(event) =>
                    setSelectedCustomer(
                      event.target.value,
                    )
                  }
                >
                  <option value="">
                    Select debtor?
                  </option>

                  {debtCustomers.map(
                    (item) => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.name} ?{' '}
                        {money(
                          customerTotals.get(
                            item.id,
                          ),
                        )}
                      </option>
                    ),
                  )}
                </select>
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Customer email
                </span>
                <input
                  className="aad-input"
                  value={
                    selected?.email ??
                    'No email saved'
                  }
                  disabled
                />
              </label>
            </div>

            <label className="aad-field">
              <span className="aad-label">
                Optional message
              </span>
              <textarea
                className="aad-input"
                maxLength={1000}
                value={reminderMessage}
                onChange={(event) =>
                  setReminderMessage(
                    event.target.value,
                  )
                }
                placeholder="Optional note to include with the payment reminder"
              />
            </label>

            <button
              className="aad-button aad-button-gold"
              disabled={
                busy ||
                !selectedCustomer ||
                !selected?.email
              }
            >
              {busy
                ? 'Sending?'
                : 'Send email reminder'}
            </button>
          </form>
        </section>

        {loading ? (
          <State title="Loading reminder history?" />
        ) : !reminders.length ? (
          <State title="No reminder history">
            Sent and failed reminder attempts will
            appear here.
          </State>
        ) : (
          <div className="aad-table-wrap">
            <table className="aad-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Recipient</th>
                  <th>Amount</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Delivery information</th>
                </tr>
              </thead>

              <tbody>
                {reminders.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {dateTime(
                        item.sent_at ??
                          item.created_at,
                      )}
                    </td>
                    <td>
                      {customer(
                        item.customer_id,
                      )?.name ??
                        item.customer_id}
                    </td>
                    <td>{item.recipient}</td>
                    <td>
                      {money(
                        item.amount_outstanding,
                      )}
                    </td>
                    <td>{item.channel}</td>
                    <td>
                      <Badge
                        value={item.status}
                      />
                    </td>
                    <td>
                      {item.status === 'FAILED'
                        ? `Delivery failed${item.error_code ? ` (${item.error_code})` : ''}`
                        : item.sent_at
                          ? `Sent ${dateTime(item.sent_at)}`
                          : 'Pending'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    )
  }


  if (mode === 'statements') {
    const selected = customer(
      selectedCustomer,
    )

    return (
      <section className="aad-page">
        <PageHeader
          title="Customer Statements"
          description="View and print chronological customer credit and collection activity."
          action={
            <div className="header-actions">
              <Link
                className="aad-button aad-button-secondary"
                to="/receivables"
              >
                Debt book
              </Link>

              <button
                className="aad-button aad-button-secondary"
                type="button"
                disabled={!selectedCustomer}
                onClick={() => window.print()}
              >
                Print statement
              </button>
            </div>
          }
        />

        {error && (
          <p className="aad-error">{error}</p>
        )}

        <section className="aad-card aad-card-padding">
          <label className="aad-field">
            <span className="aad-label">
              Customer
            </span>

            <select
              className="aad-select"
              value={selectedCustomer}
              onChange={(event) => {
                const id = event.target.value
                setSelectedCustomer(id)
                void loadStatement(id)
              }}
            >
              <option value="">
                Select customer?
              </option>

              {customers.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </section>

        {selected && (
          <section className="aad-card aad-card-padding">
            <p className="aad-muted">{workspace.business.name}</p>
            <h2 className="aad-section-title">
              {selected.name}
            </h2>

            <p>
              {selected.phone || 'No phone'}
              {' · '}
              {selected.email || 'No email'}
            </p>

            <p>
              Outstanding:{' '}
              <strong>
                {money(
                  summary?.total_outstanding ??
                    0,
                )}
              </strong>
            </p>
            <p className="aad-muted">
              Statement generated: {dateTime(new Date().toISOString())}
            </p>
          </section>
        )}

        {loading ? (
          <State title="Loading statement?" />
        ) : !selectedCustomer ? (
          <State title="Select a customer" />
        ) : !statement.length ? (
          <State title="No statement activity">
            Credit sales and collections will appear
            here.
          </State>
        ) : (
          <div className="aad-table-wrap">
            <table className="aad-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Activity</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Running balance</th>
                  <th>Reference</th>
                  <th>Sale</th>
                </tr>
              </thead>

              <tbody>
                {statement.map(
                  (item, index) => (
                    <tr
                      key={`${item.receivable_id}-${item.occurred_at}-${index}`}
                    >
                      <td>
                        {dateTime(
                          item.occurred_at,
                        )}
                      </td>
                      <td>
                        <Badge
                          value={
                            item.event_type
                          }
                        />
                      </td>
                      <td>{item.event_type === 'CREDIT_SALE' ? money(item.amount) : '—'}</td>
                      <td>{item.event_type === 'COLLECTION' ? money(item.amount) : '—'}</td>
                      <td>
                        {money(
                          item.remaining_balance,
                        )}
                      </td>
                      <td>
                        {item.reference || '—'}
                      </td>
                      <td>
                        {item.sale_id ? (
                          <Link
                            to={`/sales/${item.sale_id}`}
                          >
                            Open sale
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    )
  }


  return (
    <section className="aad-page">
      <PageHeader
        title="Debtors / Receivables"
        description="Track customer credit balances, aging and collections."
        action={
          <div className="header-actions">
            <Link
              className="aad-button aad-button-secondary"
              to="/receivables/statements"
            >
              Statements
            </Link>

            <Link
              className="aad-button aad-button-gold"
              to="/receivables/reminders"
            >
              Debt reminders
            </Link>
          </div>
        }
      />

      {error && (
        <p className="aad-error">{error}</p>
      )}

      <div className="kpi-grid">
        <article className="aad-card aad-card-padding">
          <small>Outstanding</small>
          <strong>
            {money(totalOutstanding)}
          </strong>
        </article>

        <article className="aad-card aad-card-padding">
          <small>Overdue</small>
          <strong>{money(overdueTotal)}</strong>
        </article>

        <article className="aad-card aad-card-padding">
          <small>Open debts</small>
          <strong>{openRows.length}</strong>
        </article>

        <article className="aad-card aad-card-padding">
          <small>Partially paid debts</small>
          <strong>{partiallyPaid}</strong>
        </article>
      </div>

      <section className="aad-card aad-card-padding">
        <div className="aad-form-grid">
          <input
            className="aad-input"
            placeholder="Search customer, phone or email"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />

          <select
            className="aad-select"
            value={bucket}
            onChange={(event) =>
              setBucket(event.target.value)
            }
          >
            <option value="">
              All aging buckets
            </option>
            <option value="CURRENT">
              Current
            </option>
            <option value="1_30">
              1?30 days
            </option>
            <option value="31_60">
              31?60 days
            </option>
            <option value="61_90">
              61?90 days
            </option>
            <option value="90_PLUS">
              90+ days
            </option>
          </select>

          <select
            className="aad-select"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="PARTIALLY_PAID">Partially paid</option>
            <option value="PAID">Paid</option>
          </select>

          <label className="aad-field">
            <span className="aad-label">Start date</span>
            <input className="aad-input" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </label>

          <label className="aad-field">
            <span className="aad-label">End date</span>
            <input className="aad-input" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </label>

          <label className="aad-field">
            <span className="aad-label">Overdue only</span>
            <input type="checkbox" checked={overdueOnly} onChange={(event) => setOverdueOnly(event.target.checked)} />
          </label>
        </div>
      </section>

      {loading ? (
        <State title="Loading debt book?" />
      ) : !visible.length ? (
        <State title="No receivables">
          Credit sales with outstanding balances
          appear here.
        </State>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Original</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Due</th>
                <th>Aging</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visible.map((row) => {
                const item = row.receivable
                const person = customer(
                  item.customer_id,
                )

                return (
                  <tr key={item.id}>
                    <td>
                      <strong>
                        {person?.name ??
                          'Customer'}
                      </strong>
                      <br />
                      <small className="aad-muted">
                        {person?.phone ||
                          person?.email ||
                          '?'}
                      </small>
                    </td>
                    <td>
                      {money(
                        item.original_amount,
                      )}
                    </td>
                    <td>
                      {money(
                        item.amount_paid,
                      )}
                    </td>
                    <td>
                      {money(item.balance)}
                    </td>
                    <td>
                      {item.due_date ||
                        'Not set'}
                    </td>
                    <td>
                      <Badge
                        value={bucketLabel(
                          row.aging_bucket,
                        )}
                      />
                    </td>
                    <td>
                      <Badge
                        value={item.status}
                      />
                    </td>
                    <td>{dateTime(item.created_at)}</td>
                    <td>
                      <div className="header-actions">
                        <Link className="aad-button aad-button-secondary" to={`/receivables/${item.id}`}>View / collect</Link>
                        <Link className="aad-button aad-button-secondary" to={`/receivables/statements?customer=${item.customer_id}`}>Statement</Link>
                        {Number(item.balance) > 0 && person?.email && <Link className="aad-button aad-button-secondary" to={`/receivables/reminders?customer=${item.customer_id}`}>Reminder</Link>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
